import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, CheckCircle, Building2, Factory } from 'lucide-react';

interface Address {
  id: string;
  label: string;
  type: 'company' | 'factory';
  address: string;
  contact?: string;
  phone?: string;
}

interface DeliveryAddressBlockProps {
  data: unknown;
  onUpdateFields?: (fields: Record<string, unknown>) => void;
}

const DeliveryAddressBlock: React.FC<DeliveryAddressBlockProps> = ({ data, onUpdateFields }) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const parsed = data as { addresses: Address[] } | undefined;
  const addresses = parsed?.addresses ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!addresses.length) return null;

  const handleSelect = (addr: Address) => {
    setSelectedId(addr.id);
    onUpdateFields?.({
      delivery_address: addr.address,
      delivery_address_label: addr.label,
      delivery_contact: addr.contact,
      delivery_phone: addr.phone,
    });
  };

  // Collapsed after selection
  if (selectedId) {
    const chosen = addresses.find(a => a.id === selectedId);
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs">
        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
          {isZh ? '收貨地址' : 'Delivery address'}: {chosen?.label} — {chosen?.address}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2 text-xs font-medium text-blue-700 dark:text-blue-400">
        <MapPin className="w-3.5 h-3.5" />
        {isZh ? '選擇收貨地址' : 'Select delivery address'}
      </div>

      {/* Address cards */}
      <div className="p-2 space-y-1.5">
        {addresses.map(addr => {
          const Icon = addr.type === 'company' ? Building2 : Factory;
          return (
            <button
              key={addr.id}
              onClick={() => handleSelect(addr)}
              className="w-full text-left px-3 py-2.5 rounded-md border border-gray-100 dark:border-gray-600 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:border-blue-700 dark:hover:bg-blue-900/10 transition-colors group"
            >
              <div className="flex items-start gap-2">
                <Icon className="w-3.5 h-3.5 mt-0.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{addr.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      addr.type === 'company'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                    }`}>
                      {addr.type === 'company' ? (isZh ? '公司' : 'HQ') : (isZh ? '工廠' : 'Factory')}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{addr.address}</div>
                  {addr.contact && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {addr.contact}{addr.phone ? ` · ${addr.phone}` : ''}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryAddressBlock;
